import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, Warning } from '@phosphor-icons/react';
import { cancelRegistration } from '../../api/learnPublicApi';
import TrainingTopbar, { useCatalogPath } from './TrainingTopbar';

export default function TrainingCancelPage() {
  const { cancelToken } = useParams<{ cancelToken: string }>();
  const navigate = useNavigate();
  const catalogPath = useCatalogPath();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCancel = async () => {
    if (!cancelToken) return;
    setLoading(true);
    setError(null);
    try {
      await cancelRegistration(cancelToken);
      setDone(true);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to cancel registration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <TrainingTopbar subtitle="Cancel Registration" />

      <div className="flex-1 flex items-center justify-center px-4 py-6">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-10 max-w-md w-full text-center">
          {done ? (
            <>
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-5">
                <CheckCircle size={32} weight="fill" className="text-emerald-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2 tracking-tight">
                Registration Cancelled
              </h2>
              <p className="text-sm text-gray-500 mb-7 leading-relaxed">
                Your registration has been successfully cancelled. You will receive a confirmation email shortly.
              </p>
              <Link
                to={catalogPath}
                className="inline-block px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm"
              >
                Browse Catalog
              </Link>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-5">
                <Warning size={32} weight="fill" className="text-amber-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2 tracking-tight">
                Cancel Registration?
              </h2>
              <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                Are you sure you want to cancel this registration? This action cannot be undone.
              </p>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-5 text-sm text-red-600 leading-relaxed">
                  {error}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => navigate(catalogPath)}
                  className="flex-1 px-5 py-2.5 bg-white text-gray-700 text-sm font-semibold rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                >
                  Keep Registration
                </button>
                <button
                  onClick={handleCancel}
                  disabled={loading}
                  className="flex-1 px-5 py-2.5 bg-white text-red-600 text-sm font-semibold rounded-lg border border-red-200 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Cancelling...' : 'Yes, Cancel'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
